/**
 * PresetLadder — monotonic privacy ladder (Solo → Curated → Moderated → Open).
 * Vertical so restrictiveness reads top→bottom; left rail shows the gradient
 * from "safer" to "more open"; Moderated is emphasized + ★ Recommended.
 * Reused by SignupFlow per-child presets and Parental Controls Overview.
 * Replaces the old PrivacyPresetCards grid.
 */
import { ChromeTokens } from '../chrome/tokens';
import { Icon } from '../chrome/Icon';

export interface PresetOption {
  id: string;
  label: string;
  glyph: string;
  desc: string;
  rec?: boolean;
}

/** Preset ids match backend PRESET_DEFAULTS keys (privacy_enforcer.py). */
export const PRESETS: PresetOption[] = [
  { id: 'solo', label: 'Solo', glyph: '🏠', desc: 'Just them and their creations. No visitors.' },
  // Curated really has friend requests DISABLED (privacy_enforcer.py). The
  // previous "Friends need permission" line was wrong — at Curated they
  // can see admin-vetted highlights but they have no friends at all.
  { id: 'curated', label: 'Curated', glyph: '🌟', desc: 'Only grown-up-picked highlights from world by me. No friends.' },
  // Moderated maxes share scope at "friends" (so they can NOT share
  // publicly without bumping to Open), shows curated, and enables
  // friend requests with parent approval. The footer below the ladder
  // adds the findability clarification so this line stays short.
  { id: 'moderated', label: 'Moderated', glyph: '🌈', desc: 'Search for friends and be found by them. Every friend needs your okay. Curated highlights are on.', rec: true },
  { id: 'open_with_oversight', label: 'Open + Oversight', glyph: '🌍', desc: 'Explore freely — you can always see what happens.' },
];

const METER_COLORS = [
  ChromeTokens.color.grass,
  `color-mix(in srgb, ${ChromeTokens.color.grass}, ${ChromeTokens.color.sun})`,
  ChromeTokens.color.sun,
  ChromeTokens.color.coral,
];

function OpennessMeter({ level }: { level: number }) {
  return (
    <span
      style={{ display: 'flex', flexDirection: 'column-reverse', gap: 2, width: 8 }}
      aria-hidden="true"
      title={`Openness ${level} of 4`}
    >
      {[0, 1, 2, 3].map(s => (
        <span key={s} style={{ height: 5, borderRadius: 2, background: s < level ? METER_COLORS[level - 1] : ChromeTokens.color.baseMuted }} />
      ))}
    </span>
  );
}

interface PresetLadderProps {
  value: string;
  onChange: (preset: string) => void;
  /**
   * When true, the user has fine-tune overrides on top of the active
   * preset, so the active card's description may not match reality.
   * Shows a "MODIFIED" badge and warns on switching presets that the
   * overrides will reset. Defaults to false for legacy callers.
   */
  isCustomized?: boolean;
}

export function PresetLadder({ value, onChange, isCustomized }: PresetLadderProps) {
  const handlePresetClick = (next: string) => {
    if (next === value) {
      // Re-tap on the active preset is the "reset overrides" gesture
      // (mirrors the existing "reset bundle" button below the ladder).
      onChange(next);
      return;
    }
    if (isCustomized) {
      // Switching off a modified preset will discard the overrides
      // because the backend ``PUT /privacy/me/preset`` endpoint nulls
      // every ``ps_*`` column (see backend/app/routes/privacy.py).
      // Confirm before doing it so the parent isn't surprised.
      const ok = window.confirm(
        "You've fine-tuned some settings on top of your current "
          + "privacy preset. Switching to a different preset will reset "
          + "those custom changes back to the new preset's defaults.\n\n"
          + "Continue?",
      );
      if (!ok) return;
    }
    onChange(next);
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 12 }}>
        {/* SAFER → OPEN restrictiveness rail */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 2 }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: '#3a6e3f', letterSpacing: '.04em' }}>SAFER</span>
          <div style={{ flex: 1, width: 6, borderRadius: 999, margin: '6px 0', background: `linear-gradient(180deg, ${ChromeTokens.color.grass}, ${ChromeTokens.color.sun} 55%, ${ChromeTokens.color.coral})` }} />
          <span style={{ fontSize: 10, fontWeight: 800, color: ChromeTokens.color.coral, letterSpacing: '.04em' }}>OPEN</span>
        </div>
        <div role="radiogroup" aria-label="Privacy level" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {PRESETS.map((p, i) => {
            const active = value === p.id;
            return (
              <button
                key={p.id}
                role="radio"
                aria-checked={active}
                aria-label={
                  `${p.label}: ${p.desc}`
                  + (active ? ' (current setting)' : '')
                  + (active && isCustomized ? ' — modified by fine-tune overrides' : '')
                }
                onClick={() => handlePresetClick(p.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', cursor: 'pointer',
                  padding: p.rec ? '14px' : '11px 14px', borderRadius: ChromeTokens.radius.lg,
                  background: ChromeTokens.color.base, fontFamily: ChromeTokens.font.ui,
                  border: active ? `3px solid ${ChromeTokens.color.sun}` : p.rec ? `2px solid ${ChromeTokens.color.coral}` : `2px solid ${ChromeTokens.color.baseMuted}`,
                  boxShadow: (active || p.rec) ? ChromeTokens.shadow.soft : 'none',
                }}
              >
                <OpennessMeter level={i + 1} />
                <span style={{ fontSize: p.rec ? 28 : 22, lineHeight: 1 }}>{p.glyph}</span>
                <span style={{ flex: 1 }}>
                  <span style={{ fontWeight: 800, fontSize: p.rec ? 16 : 14, display: 'inline-flex', alignItems: 'center', gap: 6, color: ChromeTokens.color.ink, flexWrap: 'wrap' }}>
                    {p.label}
                    {/* "Your current setting" — distinct from the
                        coral "RECOMMENDED" badge so the user can read
                        both at a glance when they overlap (i.e. they're
                        on the recommended preset). Muted gray so it
                        doesn't compete visually with the recommendation. */}
                    {active && (
                      <span
                        style={{
                          fontSize: 10, fontWeight: 800,
                          color: ChromeTokens.color.ink,
                          background: ChromeTokens.color.baseMuted,
                          padding: '2px 7px', borderRadius: 999,
                        }}
                      >
                        YOUR CURRENT SETTING
                      </span>
                    )}
                    {/* "MODIFIED" — appears only on the active card,
                        only when fine-tune overrides exist. Sun-colored
                        (not coral) so it doesn't read as a warning, but
                        it's bold enough that a parent reading the
                        description knows it may not match reality. */}
                    {active && isCustomized && (
                      <span
                        style={{
                          fontSize: 10, fontWeight: 800,
                          color: ChromeTokens.color.ink,
                          background: ChromeTokens.color.sun,
                          padding: '2px 7px', borderRadius: 999,
                        }}
                        title="Some settings are customized in Fine-tune. The description above may not match your actual settings."
                      >
                        MODIFIED
                      </span>
                    )}
                    {p.rec && <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: ChromeTokens.color.coral, padding: '2px 7px', borderRadius: 999 }}>★ RECOMMENDED</span>}
                  </span>
                  <div style={{ fontSize: 12, color: ChromeTokens.color.inkMuted, marginTop: 2 }}>
                    {p.desc}
                    {active && isCustomized && (
                      <span style={{ display: 'block', marginTop: 4, fontStyle: 'italic' }}>
                        Some settings are customized below in Fine-tune.
                        Switching presets will reset them.
                      </span>
                    )}
                  </div>
                </span>
                {active && <Icon name="check" size={18} stroke={ChromeTokens.color.sun} />}
              </button>
            );
          })}
        </div>
      </div>

      {/*
        Footer — three compliance-essential notes that don't fit cleanly
        inside any one preset's description:

        1. Reversibility — COPPA requires telling the parent they can
           change their mind. The existing UI implies it (the picker
           exists in Settings), but stating it explicitly is the
           defensible posture.
        2. Findability mechanic — since "friend code" doesn't exist in
           this app, the ONLY way friend requests work is via partial-
           username search. Turning on inbound friend requests on a
           preset (Moderated / Open) also makes the kid's avatar and
           username findable by anyone typing part of the username.
           Putting this here keeps the per-preset descriptions short.
        3. Weekly recap — every preset on this ladder turns the parent's
           weekly recap email on for the child, and this is where the parent
           chooses, so it is named here rather than behind a separate consent
           checkbox (weekly-parent-digest Reqs 1.6, 9.5).
      */}
      <div
        style={{
          marginTop: 14,
          padding: '10px 12px',
          borderRadius: ChromeTokens.radius.md,
          background: ChromeTokens.color.baseMuted,
          fontSize: 12,
          lineHeight: 1.5,
          color: ChromeTokens.color.inkMuted,
          fontFamily: ChromeTokens.font.ui,
        }}
      >
        <p style={{ margin: '0 0 6px', fontWeight: 700, color: ChromeTokens.color.ink }}>
          You can change this any time.
        </p>
        <p style={{ margin: 0 }}>
          When a child can receive friend requests, their avatar and
          username become findable in search — including matches on part
          of the username. That's how friends ask to be friends; there's
          no separate friend-code step.
        </p>
        <p style={{ margin: '6px 0 0' }}>
          Every option includes a weekly recap email to you about what they
          made — you can turn it off any time.
        </p>
      </div>
    </div>
  );
}
